import React from 'react'
import { Box, TextField, Typography, Divider, IconButton, InputAdornment, Stack, Chip } from '@mui/material'
import FolderOpenIcon from '@mui/icons-material/FolderOpen'
import FolderIcon from '@mui/icons-material/Folder'
import ClearIcon from '@mui/icons-material/Clear'

export interface BatchConfigPanelProps {
  prefix: string
  suffix: string
  destinationFolder: string
  onPrefixChange: (value: string) => void
  onSuffixChange: (value: string) => void
  onPickFolder?: () => void
  onClearFolder?: () => void
  prefixRequired?: boolean
  /** Hide the prefix and suffix fields. */
  hideNameFields?: boolean
}

const BatchConfigPanel: React.FC<BatchConfigPanelProps> = ({
  prefix,
  suffix,
  destinationFolder,
  onPrefixChange,
  onSuffixChange,
  onPickFolder,
  onClearFolder,
  prefixRequired = false,
  hideNameFields = false,
}) => (
  <Box sx={{
    backgroundColor: 'background.default',
    borderBottom: '1px solid',
    borderColor: 'divider',
  }}>
  <Box sx={{
    display: 'flex',
    gap: 1.5,
    alignItems: 'center',
    px: 3,
    pt: 2.5,
    pb: 1.5,
  }}>
    <Typography variant="subtitle2" sx={{
      color: 'text.secondary',
      textTransform: 'uppercase',
      letterSpacing: '0.08em',
      fontSize: '0.75rem',
      mr: 0.5,
      whiteSpace: 'nowrap',
    }}>
      Batch Settings
    </Typography>

    <Divider orientation="vertical" flexItem sx={{ my: '4px' }} />

    {!hideNameFields && (
      <>
        <TextField
          value={prefix}
          onChange={e => onPrefixChange(e.target.value)}
          label="Table Name Prefix"
          placeholder="e.g. Wave1_"
          helperText={prefixRequired && !prefix ? 'Required' : 'Added before each variable name'}
          error={prefixRequired && !prefix}
          size="small"
          sx={{ flex: 1 }}
          required={prefixRequired}
        />

        <TextField
          value={suffix}
          onChange={e => onSuffixChange(e.target.value)}
          label="Table Name Suffix"
          placeholder="e.g. _v2"
          helperText="Added after each variable name"
          size="small"
          sx={{ flex: 1 }}
        />

        <Divider orientation="vertical" flexItem sx={{ my: '4px' }} />
      </>
    )}

    {onPickFolder ? (
      <TextField
        value={destinationFolder}
        label="Destination Folder"
        helperText="All new tables will be created here"
        size="small"
        sx={{ flex: 1 }}
        InputProps={{
          readOnly: true,
          endAdornment: (
            <InputAdornment position="end">
              <Stack direction="row" spacing={0.25}>
                {onClearFolder && destinationFolder ? (
                  <IconButton size="small" onClick={onClearFolder} sx={{ p: 0.25 }} aria-label="Clear Folder">
                    <ClearIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                ) : null}
                <IconButton size="small" onClick={onPickFolder} sx={{ p: 0.25 }} aria-label="Choose Folder">
                  <FolderOpenIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </Stack>
            </InputAdornment>
          ),
        }}
      />
    ) : (
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Saving to
        </Typography>
        <Chip
          icon={<FolderIcon />}
          label={destinationFolder}
          variant="outlined"
          size="small"
          sx={{ justifyContent: 'flex-start', maxWidth: '100%' }}
        />
      </Box>
    )}
  </Box>
  </Box>
)

export default BatchConfigPanel
